import * as amplitude from "@amplitude/analytics-react-native"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { setAgentNotifCount, setIsAgentModalVisible, setSuggestedActivity, setMiniMode, setFocusedMode } from "@data/redux/actions/liveMode"
import { useRoomContext } from "@livekit/components-react"
import React, { useContext, useEffect, useState } from "react"
import { StyleSheet, View, Platform, ScrollView } from "react-native"
import Animated, { FadeIn, FadeOut, SlideInUp, SlideOutUp, SlideInDown, SlideOutDown, useSharedValue, withSpring, useAnimatedStyle, runOnJS } from "react-native-reanimated"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import { connect } from "react-redux"
import useThrottledSetAttributes from "../../engine/live/room/useThrottledSetAttributes"
import useLiveKitEventSender from "../hooks/livekit/useLiveKitEventSender"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { LinearGradient } from "expo-linear-gradient"
import ActivityScreen from "../../student/activity/execute/ActivityScreen"
import SamConversationBase from "./SamConversationBase"
import { useNavigation, useRoute } from "@react-navigation/native"
import { useCurrentActivity } from "../hooks/useCurrentActivity"
import { hasFeature } from "@data/utility/Utils"
import FilledButtonM from "@common/components/button/FilledButtonM"


const SamCheckpointOverlay = (props) => {
    const { visible, conversation, onClose } = props
    const context = conversation?.conversation?.trigger_context || conversation?.trigger_context || "raise_hand"
    const isCheckpointRHEnabled = hasFeature(props.currentLiveSession?.live_room, "checkpoint-rh")
    const isQuizContext = context === "prerequisite_quiz"

    const [showActivity, setShowActivity] = useState(false)
    const translateY = useSharedValue(0)
    const startY = useSharedValue(0)

    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()
    const navigation = useNavigation()
    const route = useRoute()
    const setAttributesThrottled = useThrottledSetAttributes()
    const room = useRoomContext()
    const { sendLiveKitData: triggerSamNotification } = useLiveKitEventSender()
    const { currentActivity } = useCurrentActivity?.() || {}

    // Setup on visibility change
    useEffect(() => {
        if (!visible) return
        amplitude.track("sam_checkpoint_overlay.open", { context })
        props.setIsAgentModalVisible?.(true)
        props.setMiniMode?.(true)
        props.setFocusedMode?.(false)
        room?.localParticipant && setAttributesThrottled({ ongoing_checkpoint: "1", display_name: props.user?.display_name }).catch(() => {})

        return () => {
            props.setIsAgentModalVisible?.(false)
            room?.localParticipant && setAttributesThrottled({ ongoing_checkpoint: "", display_name: props.user?.display_name }).catch(() => {})
        }
    }, [visible])

    const handleOverlayClose = () => {
        props.user?.id && props.currentLiveSession?.id && props.setAgentNotifCount?.(props.currentLiveSession.id, props.user.id)
        onClose?.()
    }

    const toggleActivity = () => setShowActivity(prev => !prev)

    // Gesture handler for mobile swipe down
    const panGesture = Gesture.Pan()
        .onStart(() => { startY.value = translateY.value })
        .onUpdate((event) => { if (event.translationY > 0) translateY.value = startY.value + event.translationY })
        .onEnd((event) => {
            if (translateY.value > 100 || event.velocityY > 500) runOnJS(toggleActivity)()
            translateY.value = withSpring(0)
        })

    const animatedStyle = useAnimatedStyle(() => ({ transform: [{ translateY: translateY.value }] }))

    if (!visible || (!isCheckpointRHEnabled && !isQuizContext)) {

        return null
    }


    const isWeb = Platform.OS === 'web'
    const isLargeScreen = isWeb && (dimensions?.width || 0) >= 800
    const footerSpace = props.isLiveModeEnabled ? (70 + insets.bottom) : insets.bottom

    // Get activity from hook or redux
    const activity = currentActivity?.activityData || props.currentActivity?.activityData || props.currentActivity

    const conversationWidth = isLargeScreen && showActivity ? (dimensions?.width || 0) * 0.55 - 40 : (dimensions?.width || 0) - 40
    // Deliberately not scrollable on phone: the quiz correction owns its own
    // bounded scroll area (QuizCorrectionReview), so only that part moves and
    // the "Continuer" button below it stays put.
    const ContentWrapper = isLargeScreen ? ScrollView : View
    const contentWrapperProps = isLargeScreen ? { contentContainerStyle: styles.scrollViewContent, showsVerticalScrollIndicator: false } : { style: styles.scrollViewContent }

    return (
        <Animated.View
            entering={FadeIn.duration(300)}
            exiting={FadeOut.duration(300)}
            style={[
                styles.overlayContainer,
                {
                    width: '100%',
                    height: (dimensions?.height || 0) - footerSpace,
                    top: 0,
                    left: 0,
                    paddingTop: insets.top,
                }
            ]}
        >
            <View style={styles.backdrop} />
            <LinearGradient
                colors={[
                    "#F8FAFC",
                    "#F1F5F9",
                    "#E2E8F0",
                ]}
                style={StyleSheet.absoluteFillObject}
            />
            <Animated.View
                entering={SlideInUp.duration(500)}
                exiting={SlideOutUp.duration(300)}
                style={[styles.contentContainer, isLargeScreen && showActivity && { width: '60%', right: 0, position: 'absolute' }]}
            >
                <ContentWrapper {...contentWrapperProps}>
                    <SamConversationBase
                        conversation={conversation}
                        context={context}
                        onClose={handleOverlayClose}
                        isOverlay
                        showInitialLoading
                        conversationWidth={conversationWidth}
                        setSuggestedActivity={props.setSuggestedActivity}
                        currentActivity={currentActivity || props.currentActivity}
                        room={room}
                        setAttributesThrottled={setAttributesThrottled}
                        triggerSamNotification={triggerSamNotification}
                    />

                </ContentWrapper>
            </Animated.View>

            {/* "Voir l'activité" button - fixed at very bottom (hidden during quiz) */}
            {activity && !showActivity && !isQuizContext && (
                <Animated.View
                    entering={FadeIn.duration(300).delay(500)}
                    style={[styles.viewActivityButtonContainer, { bottom: insets.bottom + 10 }]}
                >
                    <FilledButtonM
                        label="Voir l'activité"
                        icon="eye"
                        onPress={toggleActivity}
                        mode="text"
                        color={theme.colors.primary}
                        labelStyle={{ fontSize: 12, height: 16 }}
                        contentStyle={{ height: 35 }}
                    />
                </Animated.View>
            )}

            {/* "Masquer l'activité" button when activity is shown (hidden during quiz) */}
            {activity && showActivity && !isQuizContext && (
                <Animated.View
                    entering={FadeIn.duration(300)}
                    style={[
                        styles.viewActivityButtonContainer,
                        { bottom: insets.bottom + 10 },
                        isLargeScreen && { left: '40%', right: 0 }
                    ]}
                >
                    <FilledButtonM
                        label="Masquer l'activité"
                        icon="eye-slash"
                        onPress={toggleActivity}
                        mode="text"
                        color={theme.colors.grey}
                        labelStyle={{ fontSize: 12, height: 16 }}
                        contentStyle={{ height: 35 }}
                    />
                </Animated.View>
            )}

            {activity && showActivity && !isQuizContext && (
                isLargeScreen ? (
                    <Animated.View
                        entering={SlideInUp.duration(500)}
                        exiting={SlideOutUp.duration(300)}
                        style={styles.activityPanelWeb}
                    >
                        <ScrollView
                            style={styles.activityContent}
                            contentContainerStyle={{ flexGrow: 1, width: '100%' }}
                            showsVerticalScrollIndicator={true}
                        >
                            <DimensionsContext.Provider value={{
                                ...dimensions,
                                width: (dimensions?.width || 0) * 0.6
                            }}>
                                <ActivityScreen
                                    currentActivity={currentActivity || props.currentActivity}
                                    navigation={navigation}
                                    route={route}
                                    hideMyWorkButton={true}
                                    skipInitAnimation={true}
                                    isInCheckpointOverlay={true}
                                    {...props}
                                />
                            </DimensionsContext.Provider>
                        </ScrollView>
                    </Animated.View>
                ) : (
                    <Animated.View
                        entering={SlideInDown.duration(500)}
                        exiting={SlideOutDown.duration(300)}
                        style={[
                            styles.activityPanelMobile,
                            { bottom: 0 },
                        ]}
                    >
                        <Animated.View style={[{ flex: 1 }, animatedStyle]}>
                            {/* Bottom sheet header with handle bar */}
                            <GestureDetector gesture={panGesture}>
                                <Animated.View style={styles.activityHeaderMobile}>
                                    <View style={styles.activityHandleBar} />
                                </Animated.View>
                            </GestureDetector>

                            <ScrollView
                                style={[styles.activityContent, { marginTop: 0 }]}
                                contentContainerStyle={{ flexGrow: 1, paddingTop: 0 }}
                                showsVerticalScrollIndicator={true}
                            >
                                <ActivityScreen
                                    currentActivity={currentActivity || props.currentActivity}
                                    navigation={navigation}
                                    route={route}
                                    hideMyWorkButton={true}
                                    skipInitAnimation={true}
                                    isInCheckpointOverlay={true}
                                    {...props}
                                />
                            </ScrollView>
                        </Animated.View>
                    </Animated.View>
                )
            )}
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    overlayContainer: {
        position: 'absolute',
        zIndex: 8,
        elevation: 8,
    },
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    contentContainer: {
        flex: 1,
        zIndex: 2,
    },
    scrollViewContent: {
        // flexGrow on web, where this is a ScrollView contentContainerStyle and
        // `flex: 1` would pin the content to the viewport and disable scrolling;
        // flex on native, where it styles a plain View.
        ...(Platform.OS === 'web' ? { flexGrow: 1 } : { flex: 1 }),
        justifyContent: "flex-start",
        alignItems: "center",
        paddingTop: 20,
        paddingBottom: 80,
        paddingHorizontal: 20,
    },
    viewActivityButtonContainer: {
        position: "absolute",
        left: 0,
        right: 0,
        alignItems: "center",
        zIndex: 100,
        elevation: 100,
    },
    // Activity panel styles
    activityPanelWeb: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '40%',
        backgroundColor: theme.colors.white,
        borderRightWidth: 1,
        borderRightColor: 'rgba(156, 163, 175, 0.2)',
        zIndex: 2,
    },
    activityPanelMobile: {
        position: 'absolute',
        left: 0,
        right: 0,
        height: '80%',
        backgroundColor: theme.colors.white,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 8,
        zIndex: 3,
        overflow: 'hidden',
    },
    activityHeaderMobile: {
        paddingTop: 8,
        paddingBottom: 8,
        alignItems: 'center',
        backgroundColor: theme.colors.white,
    },
    activityHandleBar: {
        width: 40,
        height: 4,
        backgroundColor: 'rgba(156, 163, 175, 0.4)',
        borderRadius: 2,
    },
    activityContent: {
        flex: 1,
        width: '100%',
    },
})

const mapStateToProps = (state) => {
    return {
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        user: state.data.currentUser?.object,
        liveSessionId: state.liveMode?.liveSessionId,
        isLiveModeEnabled: state.liveMode?.isLiveModeEnabled,
        miniMode: state.liveMode?.miniMode,
        focusedMode: state.liveMode?.focusedMode,
        currentActivity: state.activity?.currentActivity,
        activityTemplates: state.data.staticData?.activity_templates,
        schoolSubjects: state.data.staticData?.school_subjects,
    }
}

const mapDispatchToProps = {
    setAgentNotifCount,
    setIsAgentModalVisible,
    setSuggestedActivity,
    setMiniMode,
    setFocusedMode,
}

export default connect(mapStateToProps, mapDispatchToProps)(SamCheckpointOverlay)
